import { useRef, useState } from "react";
import { categories } from "@/mocks/homeContent";

const SUBMIT_URL = "https://readdy.ai/api/form/daglr2jep053ijub2d40";

const steps = [
  {
    emoji: "✍️",
    title: "写下来",
    desc: "把想分享的身边事、小吐槽或感受，用几句话说清楚。",
  },
  {
    emoji: "📮",
    title: "匿名提交",
    desc: "不用注册，不用填真实身份。你只管说，剩下的交给小编。",
  },
  {
    emoji: "🌟",
    title: "精选上墙",
    desc: "通过审核的内容，会被整理进对应栏目，让更多人看见、共鸣。",
  },
];

const reassure = [
  "全程匿名：不收集任何姓名、学号或联系方式",
  "只聊事不聊人：涉及他人隐私的内容不会上墙",
  "友善第一：任何造谣、人身攻击都会被拒稿",
  "内容仅供分享：不用于任何商业用途",
];

export default function SubmitSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success">("idle");
  const [formError, setFormError] = useState("");
  const [contentLen, setContentLen] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾：若 honeypot 被填入内容，则不真正提交，仅展示与正常提交一致的成功反馈
    const honeypot = ((data.get("contact_alt") as string) || "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      form.reset();
      setContentLen(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "contact_alt") payload.append(key, String(value));
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed?.code ? "" : responseText);
      const isServerFailure =
        !response.ok ||
        parsed?.code !== "OK" ||
        /spam/i.test(serverMsg || "") ||
        /form data is spam/i.test(serverMsg || "");

      if (isServerFailure) {
        setStatus("idle");
        setFormError(
          (serverMsg && serverMsg.trim())
            ? serverMsg
            : "提交没有成功，请稍后再试。如果内容涉及不当信息，可能被系统拦截。"
        );
        return;
      }

      setStatus("success");
      form.reset();
      setContentLen(0);
    } catch {
      setStatus("idle");
      setFormError("网络好像开小差了，请检查后重新提交。");
    }
  };

  return (
    <section id="submit" className="scroll-mt-16 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-12">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
              <i className="ri-send-plane-fill" aria-hidden="true" />
              投稿箱随时开启
            </p>
            <h2 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
              你想说的，都能投
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              不知道从何说起也没关系，想到哪写到哪。这里没有完美表达的要求，
              只有愿意听的耳朵。你说你的，我们守着底线听。
            </p>

            <div className="mt-7 space-y-4">
              {steps.map((s, i) => (
                <div
                  key={s.title}
                  className="flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-xl">
                    {s.emoji}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      <span className="mr-2 text-xs text-primary-500">0{i + 1}</span>
                      {s.title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <ul className="mt-6 space-y-2.5">
              {reassure.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true" />
                  {r}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-background-200 bg-background-50 p-6 shadow-card md:p-9">
            {status === "success" ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                  <i className="ri-check-double-line" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
                  投稿成功，收到啦！
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-foreground-600">
                  感谢你的分享。小编正在认真审阅，若内容通过审核，会尽快整理进对应栏目，
                  让更多人看到你的故事。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 whitespace-nowrap rounded-full border border-background-300 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  再投一条
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-primary-600">
                    <i className="ri-edit-line" aria-hidden="true" />
                  </span>
                  <h3 className="font-heading text-xl font-semibold text-foreground-950">
                    匿名投稿箱
                  </h3>
                  <span className="ml-auto rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-900">
                    无需注册
                  </span>
                </div>

                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form="校园匿名投稿表单"
                  className="mt-6 space-y-5"
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="nickname"
                        className="mb-2 block text-sm font-medium text-foreground-800"
                      >
                        怎么称呼你 <span className="text-foreground-400">(选填，可匿名)</span>
                      </label>
                      <input
                        id="nickname"
                        name="nickname"
                        type="text"
                        autoComplete="off"
                        maxLength={20}
                        placeholder="昵称 / 外号，不填也没关系"
                        className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="category"
                        className="mb-2 block text-sm font-medium text-foreground-800"
                      >
                        内容栏目 <span className="text-primary-600">*</span>
                      </label>
                      <select
                        id="category"
                        name="category"
                        required
                        defaultValue=""
                        className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      >
                        <option value="" disabled>
                          选择一条栏目
                        </option>
                        {categories.map((c) => (
                          <option key={c.key} value={c.label}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="title"
                      className="mb-2 block text-sm font-medium text-foreground-800"
                    >
                      一句话主题 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      required
                      autoComplete="off"
                      maxLength={40}
                      placeholder="比如：食堂阿姨的“手抖”终于对我失灵了"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label
                        htmlFor="content"
                        className="text-sm font-medium text-foreground-800"
                      >
                        展开说说 <span className="text-primary-600">*</span>
                      </label>
                      <span className="text-xs text-foreground-400">{contentLen}/500</span>
                    </div>
                    <textarea
                      id="content"
                      name="content"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="写下你的身边事、小吐槽或被暖到的瞬间……（建议去掉能识别出具体同学的信息）"
                      className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      onChange={(e) => setContentLen(e.target.value.length)}
                    />
                  </div>

                  <div className="form-guard" aria-hidden="true">
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <label className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <input
                      type="checkbox"
                      name="agreement"
                      required
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[oklch(var(--primary-500))]"
                    />
                    <span>
                      我已阅读并认同
                      <a href="#rule" className="mx-1 text-primary-600 underline-offset-2 hover:underline">
                        吃瓜守则
                      </a>
                      ，承诺不泄露他人隐私、不传播未经证实的消息。
                    </span>
                  </label>

                  {formError && (
                    <p
                      role="alert"
                      className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
                    >
                      <i className="ri-error-warning-line mr-1.5" aria-hidden="true" />
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-8 py-3.5 text-base font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin" aria-hidden="true" />
                        正在提交…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2" aria-hidden="true" />
                        匿名投出我的故事
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-foreground-400">
                    提交即代表你已了解并同意匿名与审核规则
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}